import { useNavigate, useParams } from 'react-router';
import { FileText, Save, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { useTemplateEditor } from './useTemplateEditor';
import { AdBar } from './AdBar';
import { FieldPalette } from './FieldPalette';
import { RowPicker } from './RowPicker';
import { CampaignTab } from './tabs/CampaignTab';
import { HeadlinesTab } from './tabs/HeadlinesTab';
import { DescriptionsTab } from './tabs/DescriptionsTab';
import { KeywordsTab } from './tabs/KeywordsTab';
import { UrlsTab } from './tabs/UrlsTab';
import { SummaryTab } from './tabs/SummaryTab';
import { ExtensionsTab } from './tabs/ExtensionsTab';
import type { EditorReady } from './tabs/types';

export default function TemplateSetEditor() {
  const { templateSetId, projectId } = useParams();
  const navigate = useNavigate();
  const ed = useTemplateEditor(templateSetId);

  if (ed.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-3">
          <FileText className="size-12 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (!ed.templateSet) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4">
          <AlertCircle className="size-12 mx-auto text-destructive" />
          <h2 className="text-xl font-semibold">Template set not found</h2>
          <Button onClick={() => navigate(`/projects/${projectId}/templates`)}>Back to Templates</Button>
        </div>
      </div>
    );
  }

  // Once past the guard, narrow templateSet so child tabs receive a non-null reference.
  const edReady: EditorReady = { ...ed, templateSet: ed.templateSet };
  const { templateSet } = edReady;

  // Plain consts (not useMemo): they sit after the early exits above, so memoizing
  // here would change hook count between renders (React #310).

  const filledHeadlines = ed.activeHeadlines.filter(s => s.variants.some(v => v.template_string.trim())).length;
  const filledDescriptions = ed.activeDescriptions.filter(s => s.variants.some(v => v.template_string.trim())).length;
  const keywordCount = templateSet.template_content.keywords.length;
  const hasUrl = !!templateSet.template_content.final_url_template?.trim();
  const hasCampaignName = !!templateSet.template_content.campaign_name_template?.trim();
  const hasAdGroupName = !!templateSet.template_content.ad_group_name_template?.trim();

  const previewHeadlines = ed.activeHeadlines
    .map(s => s.variants.find(v => v.enabled && v.template_string.trim()))
    .filter(Boolean)
    .map(v => ed.renderWithRow(v!.template_string, ed.previewRow))
    .filter(h => h.trim());

  const previewDescriptions = ed.activeDescriptions
    .map(s => s.variants.find(v => v.enabled && v.template_string.trim()))
    .filter(Boolean)
    .map(v => ed.renderWithRow(v!.template_string, ed.previewRow))
    .filter(d => d.trim());

  const previewUrl = ed.renderWithRow(templateSet.template_content.final_url_template ?? '', ed.previewRow);
  const previewPath1 = ed.renderWithRow(templateSet.template_content.path1_template ?? '', ed.previewRow);
  const previewPath2 = ed.renderWithRow(templateSet.template_content.path2_template ?? '', ed.previewRow);
  const previewDisplayUrl = (() => {
    if (!previewUrl) return '';
    try {
      const u = new URL(previewUrl);
      const paths = [previewPath1, previewPath2].filter(Boolean);
      return `${u.hostname}${paths.length ? ' › ' + paths.join(' › ') : ''}`;
    } catch { return previewUrl; }
  })();

  const adBar = (
    <AdBar
      currentAds={ed.currentAds}
      activeAdIndex={ed.activeAdIndex}
      setActiveAdIndex={ed.setActiveAdIndex}
      adCount={ed.adCount}
      isMultiAd={ed.isMultiAd}
      showCopyMenu={ed.showCopyMenu}
      setShowCopyMenu={ed.setShowCopyMenu}
      copyMenuRef={ed.copyMenuRef}
      addAd={ed.addAd}
      removeAd={ed.removeAd}
    />
  );

  const fieldPalette = (
    <FieldPalette
      selectedFields={ed.selectedFields}
      templatePlaceholders={templateSet.template_content.template_placeholders ?? {}}
      activeInputId={ed.activeInputId}
      onChipMouseDown={ed.handleChipMouseDown}
      onFieldInsert={ed.handleFieldInsert}
    />
  );

  const rowPicker = (
    <RowPicker
      rowPickerRef={ed.rowPickerRef}
      previewRow={ed.previewRow}
      previewRowIdx={ed.previewRowIdx}
      setPreviewRowIdx={ed.setPreviewRowIdx}
      rowSearch={ed.rowSearch}
      setRowSearch={ed.setRowSearch}
      showRowPicker={ed.showRowPicker}
      setShowRowPicker={ed.setShowRowPicker}
      filteredCities={ed.filteredCities}
      cityIndexMap={ed.cityIndexMap}
      rowDisplayLabel={ed.rowDisplayLabel}
      rowMatchHint={ed.rowMatchHint}
    />
  );

  const liveAdPreviewMini = (() => {
    const h = previewHeadlines.slice(0, 3).join(' | ') || null;
    const d = previewDescriptions[0] || null;
    if (!h && !d && !previewDisplayUrl) return null;
    return (
      <div className="mb-4 rounded-lg border border-border/50 bg-white dark:bg-[#1c1c2e] p-3 flex items-start gap-4">
        <div className="shrink-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] border border-[#006621] dark:border-[#4caf50] text-[#006621] dark:text-[#4caf50] rounded px-1 leading-tight font-medium">Ad</span>
            <span className="text-[11px] text-[#5f6368] dark:text-[#9aa0a6] truncate max-w-[220px]">
              {previewDisplayUrl || <span className="italic opacity-50">no url</span>}
            </span>
          </div>
          <p className="text-[#1a0dab] dark:text-[#8ab4f8] text-sm font-normal leading-tight mb-0.5 font-sans">
            {h || <span className="text-muted-foreground/30 italic text-xs">no headlines yet</span>}
          </p>
          {d && <p className="text-xs text-[#4d5156] dark:text-[#bdc1c6] leading-snug">{d}</p>}
        </div>
        <div className="ml-auto shrink-0 flex items-center gap-1.5">
          {rowPicker}
        </div>
      </div>
    );
  })();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}/templates`)}>
            <ArrowLeft className="size-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-display">{templateSet.name}</h1>
            <p className="text-[11px] text-muted-foreground font-mono uppercase tracking-widest mt-0.5">
              {templateSet.language_code.toUpperCase()} · {ed.cities.length.toLocaleString()} rows
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ed.hasUnsavedChanges && <span className="text-xs text-muted-foreground/60 font-mono">unsaved</span>}
          <Button onClick={ed.handleSave} disabled={ed.isSaving || !ed.hasUnsavedChanges} size="sm">
            <Save className="size-3.5 mr-1.5" />
            {ed.isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="campaign" className="space-y-0">
        <TabsList className="grid w-full grid-cols-7 mb-6">
          <TabsTrigger value="campaign">Campaign</TabsTrigger>
          <TabsTrigger value="headlines">
            Headlines
            {filledHeadlines > 0 && <span className="ml-1 text-[10px] font-mono opacity-50">{filledHeadlines}</span>}
            {ed.isMultiAd && <span className="ml-0.5 text-[9px] font-mono text-primary/50">×{ed.adCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="descriptions">
            Descriptions
            {filledDescriptions > 0 && <span className="ml-1 text-[10px] font-mono opacity-50">{filledDescriptions}</span>}
            {ed.isMultiAd && <span className="ml-0.5 text-[9px] font-mono text-primary/50">×{ed.adCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="keywords">
            Keywords
            {keywordCount > 0 && <span className="ml-1 text-[10px] font-mono opacity-50">{keywordCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="urls">URLs</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="extensions" disabled className="opacity-40 cursor-not-allowed">Extensions</TabsTrigger>
        </TabsList>

        <CampaignTab ed={edReady} />
        <HeadlinesTab
          ed={edReady}
          adBar={adBar}
          fieldPalette={fieldPalette}
          liveAdPreviewMini={liveAdPreviewMini}
          filledHeadlines={filledHeadlines}
        />
        <DescriptionsTab
          ed={edReady}
          adBar={adBar}
          fieldPalette={fieldPalette}
          liveAdPreviewMini={liveAdPreviewMini}
          filledDescriptions={filledDescriptions}
        />
        <KeywordsTab
          ed={edReady}
          fieldPalette={fieldPalette}
          rowPicker={rowPicker}
          keywordCount={keywordCount}
        />
        <UrlsTab ed={edReady} adBar={adBar} fieldPalette={fieldPalette} />
        <SummaryTab
          ed={edReady}
          adBar={adBar}
          rowPicker={rowPicker}
          previewHeadlines={previewHeadlines}
          previewDescriptions={previewDescriptions}
          previewDisplayUrl={previewDisplayUrl}
          filledHeadlines={filledHeadlines}
          filledDescriptions={filledDescriptions}
          keywordCount={keywordCount}
          hasUrl={hasUrl}
          hasCampaignName={hasCampaignName}
          hasAdGroupName={hasAdGroupName}
        />
        {/* Extensions tab trigger is disabled but the content is preserved */}
        <ExtensionsTab ed={edReady} />
      </Tabs>
    </div>
  );
}
